/**
 * OAuth2 authentication utilities
 * Common OAuth2 flow utilities for all apps
 */

import { generateCodeVerifier, generateCodeChallenge } from './pkce';

// OAuth2 configuration interface
export interface OAuth2Config {
  clientId: string;
  responseType: string;
  scopes: string[];
  authServerUrl: string;
  redirectUri: string;
}

// Default OAuth2 configuration
export const DEFAULT_OAUTH2_CONFIG: Partial<OAuth2Config> = {
  clientId: 'app-client',
  responseType: 'code',
  scopes: ['openid', 'profile', 'email', 'api.read', 'offline_access'],
};

/**
 * Generate a random state for CSRF protection
 */
export function generateOAuthState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * OAuth2 provider types
 */
export type OAuth2Provider = 'kakao' | 'google' | 'naver' | 'internal';

/**
 * OAuth2 flow parameters
 */
export interface OAuth2FlowParams {
  provider?: OAuth2Provider;
  state?: string;
  codeVerifier?: string;
  sessionId?: string;
  originalUrl?: string;
  displayName?: string;
  displayIcon?: string;
  lang?: string;
  prompt?: string;
}

/**
 * Build OAuth2 authorization URL with PKCE and i18n support
 * @param config OAuth2 configuration
 * @param params OAuth2 flow parameters including language setting
 * @param codeChallenge PKCE code challenge
 * @returns Complete authorization URL with all necessary parameters
 */
export function buildAuthorizationUrl(
  config: OAuth2Config,
  params: OAuth2FlowParams,
  codeChallenge: string
): string {
  const url = new URL(`${config.authServerUrl}/oauth2/authorize`);
  
  // Required OAuth2 parameters
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('response_type', config.responseType);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('scope', config.scopes.join(' '));
  
  // PKCE parameters
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  
  // State for CSRF protection
  if (params.state) {
    url.searchParams.set('state', params.state);
  }
  
  // Optional parameters
  if (params.provider && params.provider !== 'internal') {
    url.searchParams.set('identity_provider', params.provider);
  }
  
  if (params.prompt) {
    url.searchParams.set('prompt', params.prompt);
  }
  
  if (params.displayName) {
    url.searchParams.set('display_name', params.displayName);
  }
  
  if (params.displayIcon) {
    url.searchParams.set('display_icon', params.displayIcon);
  }
  
  // Language parameter for i18n support
  if (params.lang) {
    url.searchParams.set('lang', params.lang);
  }
  
  return url.toString();
}

/**
 * Initiate OAuth2 authentication flow with automatic language detection
 * Automatically detects current language from i18next, document.lang, or navigator
 * @param provider OAuth2 provider (kakao, google, naver, internal)
 * @param config Optional OAuth2 configuration overrides
 * @param prompt Optional prompt parameter (e.g., 'login' to force re-authentication)
 */
export async function initiateOAuth2Flow(
  provider: OAuth2Provider = 'kakao',
  config?: Partial<OAuth2Config>,
  prompt?: string,
  postLoginRedirect?: string
): Promise<void> {
  const fullConfig: OAuth2Config = {
    clientId: config?.clientId || DEFAULT_OAUTH2_CONFIG.clientId!,
    responseType: config?.responseType || DEFAULT_OAUTH2_CONFIG.responseType!,
    scopes: config?.scopes || DEFAULT_OAUTH2_CONFIG.scopes!,
    authServerUrl: config?.authServerUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://auth.ugot.uk'),
    redirectUri: config?.redirectUri || (typeof window !== 'undefined' ? window.location.origin + '/oauth2/redirect' : 'http://localhost:3000/oauth2/redirect'),
  };
  
  console.log(`[OAuth2] Initiating ${provider} OAuth2 flow`);
  
  // Get current language setting for i18n support
  const currentLang = typeof window !== 'undefined' ? 
    (localStorage.getItem('i18nextLng') || 
     document.documentElement.lang || 
     navigator.language.split('-')[0] || 
     'ko') : 'ko';
  
  // Generate and store state (includes original URL)
  const stateData = {
    random: generateOAuthState(),
    originalUrl: postLoginRedirect || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'),
    provider
  };
  const state = btoa(JSON.stringify(stateData))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  if (typeof window !== 'undefined') {
    localStorage.setItem('oauth2_state', state);
  }
  
  // Generate and store PKCE
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  if (typeof window !== 'undefined') {
    localStorage.setItem('pkce_code_verifier', codeVerifier);
    
    // Generate session ID for duplicate prevention
    const sessionId = crypto.randomUUID();
    localStorage.setItem('oauth2_session_id', sessionId);

    const originalUrl = stateData.originalUrl || '/';
    localStorage.setItem('oauth2_post_login_redirect', originalUrl);
  }
  
  // Build authorization URL
  const authUrl = buildAuthorizationUrl(fullConfig, {
    provider,
    state,
    displayName: 'App',
    displayIcon: '🔐',
    lang: currentLang,
    prompt: prompt,
  }, codeChallenge);
  
  console.log('[OAuth2] Redirecting to:', authUrl);
  
  // Redirect to auth server
  if (typeof window !== 'undefined') {
    window.location.href = authUrl;
  }
}

/**
 * Login with redirect helper
 * Convenience function for backward compatibility
 */
export function loginWithRedirect(
  provider: OAuth2Provider = 'kakao',
  config?: Partial<OAuth2Config>,
  prompt?: string,
  postLoginRedirect?: string
): void {
  initiateOAuth2Flow(provider, config, prompt, postLoginRedirect).catch(error => {
    console.error('[OAuth2] Failed to initiate OAuth2 flow:', error);
    throw error;
  });
}
